/**
 * The character actor: four stacked sprites that walk a tile path.
 *
 * Two things here are deliberate and worth not "simplifying" later:
 *
 * **The walk clip is driven by distance, not by time.** The animation phase is a
 * function of how far the actor has travelled, so a foot planted in frame 3 is
 * planted at the same ground position no matter what speed the actor moves at.
 * Advancing frames on a timer instead is what produces the classic foot-slide,
 * and it is the first thing a critic looking at motion will catch.
 *
 * **Depth is the feet anchor's tile.** `zIndex` is computed with the same
 * expression `getFurnitureDepth()` uses for small furniture
 * (`src/domain/depthMath.ts:230`), so the character sorts against sofas and
 * tables by exactly the rule they sort against each other by.
 */
import * as PIXI from 'pixi.js';

import { computeActorZIndex } from '../../../domain/depthMath';
import { HALF_TILE_HEIGHT, tileToWorld } from '../../../domain/grid';
import type { SceneNode } from '../../../domain/sceneNode';
import {
  CHARACTER_LAYERS,
  anchorFor,
  type CharacterLayer,
  type ClipName,
  type RecipeSheets,
} from './atlas';
import { DIRECTIONS, directionForDelta, type Direction } from './facing';
import type { TileXY } from './pathfinding';

/**
 * How fast the actor may swing its facing, in 45° sprite steps per second.
 *
 * There is no turn-in-place clip in the pack, so the turn has to be built from
 * the eight facings themselves. Without a rate limit the facing is whatever
 * `directionForDelta` says on the first frame of a path, which is how the round-1
 * capture caught `nw → se` — a 180° flip inside one 42 ms frame. At 12 steps/s
 * the same flip takes four frames of sprite (~330 ms) and reads as a pivot.
 */
const TURN_RATE_DIRS_PER_SEC = 12;

/**
 * Facing error (in 45° steps) above which the actor turns *before* it walks.
 * Anything at or under one step is a course correction and is taken while
 * moving; more than that is a real about-face, and walking through it would
 * slide the body sideways across the floor.
 */
const TURN_IN_PLACE_STEPS = 1;

/** Sub-tiles per second. One sub-tile is ~0.153 m, so this is ~1.1 m/s. */
export const WALK_SPEED_TILES = 7.0;

/**
 * Sub-tiles covered by one full walk cycle of the clip.
 *
 * Measured, not guessed: `steps/08-atlas-and-game/build.mjs --probe` sweeps the
 * retargeted `Walk_Loop` and reports the widest separation of the two feet —
 * one step — as 0.769 m, so a two-step cycle covers 1.539 m. At 0.153 m per
 * sub-tile that is 10.06 sub-tiles. Re-run the probe and update this if the
 * walk clip is ever replaced; speed is free to change without touching it.
 */
export const WALK_CYCLE_TILES = 10.06;

export interface ActorOptions {
  sheets: RecipeSheets;
  /** starting position, in continuous sub-tile coordinates */
  tileX: number;
  tileY: number;
  facing?: Direction;
  /**
   * Live furniture markers, read on every transform sync to place the actor in
   * the scene's depth order. A getter rather than a snapshot: markers keep
   * arriving for several frames after the room opens, and furniture zIndexes are
   * rewritten by `recomputeAllZIndexes` whenever an item moves.
   */
  getFurnitureMarkers?: () => Map<number, SceneNode>;
}

export class CharacterActor {
  readonly container: PIXI.Container;

  private readonly sprites = new Map<CharacterLayer, PIXI.Sprite>();
  private readonly sheets: RecipeSheets;
  private readonly getFurnitureMarkers?: () => Map<number, SceneNode>;

  tileX: number;
  tileY: number;
  facing: Direction;

  private path: TileXY[] = [];
  private pathIndex = 0;
  /**
   * Position inside the walk cycle, in cycles. Travelling advances it by
   * distance (so the feet grip the floor); pivoting on the spot advances it by
   * time (so the legs keep moving even though the body does not).
   */
  private walkPhase = 0;
  private idleTime = 0;
  /** Continuous position on the 8-facing ring; `facing` is this, rounded. */
  private headingRing = 0;
  /** True while the actor is pivoting on the spot before a walk. */
  private turning = false;
  /**
   * Dev probe only: hold the clip on its current frame. Telling a renderer that
   * is unstable from an animation whose frames simply differ needs the same
   * pose drawn twice, and the only way to get that is to stop the clock. Every
   * read of it is behind `import.meta.env.DEV`, so production drops the branch.
   */
  private frozen = false;

  constructor(opts: ActorOptions) {
    this.sheets = opts.sheets;
    this.tileX = opts.tileX;
    this.tileY = opts.tileY;
    this.facing = opts.facing ?? 's';
    this.headingRing = DIRECTIONS.indexOf(this.facing);
    this.getFurnitureMarkers = opts.getFurnitureMarkers;

    this.container = new PIXI.Container();
    this.container.label = 'sims-character';
    // `supersample` is texels per room pixel, decided by the packer's
    // `--downscale`. The art is authored against the room's pixel scale, so the
    // drawn size must stay the same however coarsely the sheet is stored: the
    // atlas is a texel budget knob (40 MB → 20 MB at downscale 3), never a
    // resize of the character. Scale the container, not each layer sprite —
    // every layer hangs off the same feet anchor at the origin, so one scale
    // keeps them registered and leaves the depth math (which reads the
    // container position) untouched.
    const texelScale = 1 / (this.sheets.supersample || 1);
    this.container.scale.set(texelScale);
    // Never intercept pointer events: taps on the floor must reach the floor so
    // click-to-move still works when the tap lands on the character itself.
    this.container.eventMode = 'none';

    for (const layer of CHARACTER_LAYERS) {
      if (!this.sheets.layers[layer]) continue;
      const sprite = new PIXI.Sprite();
      sprite.eventMode = 'none';
      this.container.addChild(sprite);
      this.sprites.set(layer, sprite);
    }

    this.applyFrame();
    this.syncTransform();
  }

  get isWalking(): boolean {
    return this.pathIndex < this.path.length;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }

  /** Start walking a (already simplified) tile path. */
  setPath(path: TileXY[]): void {
    // Drop a leading waypoint we are already standing on, or the actor spends
    // its first frames turning towards its own feet.
    const trimmed =
      path.length > 1 &&
      Math.abs(path[0].tileX - this.tileX) < 0.51 &&
      Math.abs(path[0].tileY - this.tileY) < 0.51
        ? path.slice(1)
        : path;
    this.path = trimmed;
    this.pathIndex = 0;
  }

  stop(): void {
    this.path = [];
    this.pathIndex = 0;
    this.turning = false;
  }

  /** Advance by `dt` seconds. */
  update(dt: number): void {
    if (import.meta.env.DEV && this.frozen) {
      this.applyFrame();
      this.syncTransform();
      return;
    }
    if (this.isWalking) {
      this.advanceAlongPath(dt);
    } else {
      this.idleTime += dt;
    }
    this.applyFrame();
    this.syncTransform();
  }

  /**
   * Rotate `headingRing` towards `wanted` by at most this frame's turn budget,
   * along the shorter way around the ring. Returns the error that is left, in
   * 45° steps, so the caller can decide whether it may walk yet.
   */
  private turnTowards(wanted: Direction, dt: number): number {
    const n = DIRECTIONS.length;
    const target = DIRECTIONS.indexOf(wanted);
    let delta = ((target - this.headingRing + n / 2 + n) % n) - n / 2;
    const budget = TURN_RATE_DIRS_PER_SEC * dt;
    if (Math.abs(delta) > budget) delta = Math.sign(delta) * budget;
    this.headingRing = (this.headingRing + delta + n) % n;
    this.facing = DIRECTIONS[Math.round(this.headingRing) % n];
    const left = ((target - this.headingRing + n / 2 + n) % n) - n / 2;
    return Math.abs(left);
  }

  private advanceAlongPath(dt: number): void {
    let budget = WALK_SPEED_TILES * dt;

    while (budget > 0 && this.pathIndex < this.path.length) {
      const target = this.path[this.pathIndex];
      const dx = target.tileX - this.tileX;
      const dy = target.tileY - this.tileY;
      const dist = Math.hypot(dx, dy);

      if (dist <= 1e-4) {
        this.pathIndex += 1;
        continue;
      }

      // Face the segment being walked, not the final destination — but get there
      // at a finite rate, and stay on the spot while the swing is a big one.
      const error = this.turnTowards(directionForDelta(dx, dy), dt);
      if (error > TURN_IN_PLACE_STEPS) {
        // Pivot: the legs keep cycling (a step in place, not a frozen slide),
        // the body does not travel.
        this.turning = true;
        // No travel, so distance cannot drive the legs. Play the cycle at the
        // rate the animator authored instead: half of a travel speed that is
        // itself zero is still zero, which is how the sprite used to sit frozen
        // mid-stride for the whole pivot.
        this.walkPhase += this.walkCyclesPerSecond() * dt;
        return;
      }
      this.turning = false;

      const step = Math.min(budget, dist);
      this.tileX += (dx / dist) * step;
      this.tileY += (dy / dist) * step;
      this.walkPhase += step / WALK_CYCLE_TILES;
      budget -= step;

      if (step >= dist - 1e-6) {
        this.tileX = target.tileX;
        this.tileY = target.tileY;
        this.pathIndex += 1;
      }
    }

    if (this.pathIndex >= this.path.length) {
      this.path = [];
      this.pathIndex = 0;
      this.idleTime = 0;
      this.turning = false;
    }
  }

  /**
   * Frames per second for the idle clip, taken from the atlas: the packer
   * divides the sheet's frame count by the source clip's real duration, so a
   * 16-frame sample of a ~10 s breathing loop plays at ~1.6 fps — the speed the
   * animator authored. A hard-coded rate here is what made the character
   * vibrate. `frames` is the fallback for an atlas built before `clip_fps`.
   */
  private idleFps(frames: number): number {
    const measured = this.sheets.clipFps?.['Idle_Loop'];
    return measured && measured > 0 ? measured : frames / 2;
  }

  /**
   * Idle frame index, ping-ponged: 0…n-1 then n-2…1.
   *
   * The walk take is a true cycle, so its last frame flows into its first. The
   * breathing idle is not — Mixamo's take drifts, and the best sub-window the
   * packer can find still wraps with a step ~1.4x the largest step inside the
   * window (measured on the rendered pixels, direction `s`). Played as a plain
   * loop that reads as a hitch once per cycle, which is the "плавно…потім
   * різко на дефолт" from the play test. Walking the sheet back down instead
   * makes the seam impossible by construction and doubles the visible period,
   * which suits breathing. Reversing is only sound for a motion with no
   * handedness — do not reuse this for the walk.
   */
  private idleIndex(frames: number): number {
    const raw = Math.floor(this.idleTime * this.idleFps(frames));
    if (frames <= 2) return raw % Math.max(frames, 1);
    const period = 2 * frames - 2;
    const k = ((raw % period) + period) % period;
    return k < frames ? k : period - k;
  }

  /** Frames in the walk sheet for the direction currently being drawn. */
  private walkFrameCount(): number {
    for (const layer of this.sprites.keys()) {
      const names = this.sheets.layers[layer]?.clips['Walk_Loop']?.[this.facing];
      if (names && names.length) return names.length;
    }
    return 0;
  }

  /**
   * Walk cycles per second at the animator's authored tempo, used when the legs
   * cycle without the body travelling. Falls back to the tempo that travelling
   * produces, so an atlas without `clip_fps` keeps its old behaviour.
   */
  private walkCyclesPerSecond(): number {
    const fps = this.sheets.clipFps?.['Walk_Loop'];
    const frames = this.walkFrameCount();
    if (!fps || fps <= 0 || !frames) return WALK_SPEED_TILES / WALK_CYCLE_TILES;
    return fps / frames;
  }

  private currentFrameName(layer: CharacterLayer): string | null {
    const sheet = this.sheets.layers[layer];
    if (!sheet) return null;
    const clip: ClipName = this.isWalking ? 'Walk_Loop' : 'Idle_Loop';
    const byDir = sheet.clips[clip];
    const names = byDir?.[this.facing];
    if (!names || names.length === 0) return null;

    const index = this.isWalking
      ? Math.floor((this.walkPhase % 1) * names.length)
      : this.idleIndex(names.length);
    return names[(index + names.length) % names.length];
  }

  private applyFrame(): void {
    for (const [layer, sprite] of this.sprites) {
      const sheet = this.sheets.layers[layer];
      const name = this.currentFrameName(layer);
      if (!sheet || !name) {
        sprite.visible = false;
        continue;
      }
      const texture = sheet.spritesheet.textures[name];
      if (!texture) {
        sprite.visible = false;
        continue;
      }
      sprite.visible = true;
      sprite.texture = texture;
      const a = anchorFor(texture, sheet.anchor);
      sprite.anchor.set(a.x, a.y);
    }
  }

  private syncTransform(): void {
    const { x, y } = tileToWorld(this.tileX, this.tileY);
    this.container.position.set(x, y);
    if (import.meta.env.DEV) {
      // Dev-only probe. The Stage 7 walk verification reads this and checks it
      // against a blocker map rebuilt from the database, so the two sides of the
      // check never share a source. Stripped from production builds.
      (window as unknown as Record<string, unknown>).__SIMS_CHARACTER = {
        tileX: this.tileX,
        tileY: this.tileY,
        facing: this.facing,
        // Continuous ring position, so a capture can prove the facing swings at
        // a finite rate instead of only sampling the rounded sprite direction.
        heading: this.headingRing,
        walking: this.isWalking,
        turning: this.turning,
        // The sprite frame actually being drawn, as a function so a poll reads
        // the live value: the probe object is only rebuilt when the transform
        // syncs, but an idle character animates without ever moving.
        frame: () => this.currentFrameName('body'),
        // The tile the game actually decided to walk to. A capture that only
        // sees where the character came to rest cannot tell a refused click
        // apart from a path that quietly ended somewhere else.
        destination: this.path.length ? this.path[this.path.length - 1] : null,
        // Teleport, for the depth audit only: it sweeps the character across
        // hundreds of tiles and comparing each one by actually walking there
        // would take minutes and could never reach a tile the pathfinder
        // refuses. Placement bypasses no depth logic — zIndex is still derived
        // by the same `syncTransform` the walk uses.
        place: (tileX: number, tileY: number, facing?: Direction) => {
          this.stop();
          this.tileX = tileX;
          this.tileY = tileY;
          if (facing) {
            this.facing = facing;
            this.headingRing = DIRECTIONS.indexOf(facing);
          }
          this.applyFrame();
          this.syncTransform();
        },
        // Stop the clip where it stands, so a burst of captures all show the
        // same pose. Anything that still changes between them is the renderer,
        // not the animation.
        hold: (on: boolean) => {
          this.frozen = on;
        },
        // Where the artwork actually lands on screen. The container origin must
        // coincide with the tile the logic thinks it occupies, and the sprite's
        // bottom edge is the feet — comparing the two is how a wrong atlas
        // anchor is told apart from a wrong tile.
        probeOrigin: () => {
          const p = this.container.getGlobalPosition();
          return [p.x, p.y];
        },
        probeBounds: () => {
          const b = this.container.getBounds();
          return { x: b.x, y: b.y, width: b.width, height: b.height };
        },
        // How the GPU is actually sampling the sheet. Asking the texture source
        // is the only way to tell a filter that was *set* from a filter that
        // took effect: PIXI copies the fields into a style object at upload,
        // and a mipmap flag flipped after that upload changes nothing.
        probeTexture: () => {
          const sprite = [...this.sprites.values()][0];
          const source = sprite?.texture?.source;
          if (!source) return null;
          const resource = (source as unknown as { resource?: unknown }).resource;
          return {
            width: source.width,
            height: source.height,
            // A compressed source carries its mip chain as an array of level
            // buffers. If the declared `mipLevelCount` is larger than the
            // number of levels actually uploaded, the GL texture is
            // mip-incomplete and every minified sample is undefined — which is
            // what a speckled halo looks like on a phone.
            format: source.format,
            alphaMode: source.alphaMode,
            uploadMethodId: (source as unknown as { uploadMethodId?: string }).uploadMethodId,
            uploadedLevels: Array.isArray(resource) ? resource.length : null,
            autoGenerateMipmaps: source.autoGenerateMipmaps,
            mipLevelCount: source.mipLevelCount,
            minFilter: source.style.minFilter,
            magFilter: source.style.magFilter,
            mipmapFilter: source.style.mipmapFilter,
            drawnScale: this.container.worldTransform.a,
          };
        },
        // `probeBounds` is the sprite's *frame*, transparent padding included,
        // and the frame of a walk sheet is sized for the widest stride of every
        // direction — so reading a body radius off it overstates the body by
        // more than double. The honest measurement is the drawn pixels, which
        // only the compositor knows: hide the actor, grab the frame, show it,
        // grab it again, and the difference is exactly the silhouette.
        setVisible: (v: boolean) => {
          this.container.visible = v;
        },
        // Sorting evidence: a correct zIndex means nothing if the character
        // hangs in a different parent than the furniture, or if that parent
        // does not sort at all. Reports the whole ancestor chain.
        probeZ: () => {
          const chain: unknown[] = [];
          let n: PIXI.Container | null = this.container;
          while (n) {
            chain.push({
              label: n.label ?? n.constructor.name,
              z: n.zIndex,
              sortable: n.sortableChildren,
              kids: n.children?.length ?? 0,
            });
            n = n.parent as PIXI.Container | null;
          }
          const parent = this.container.parent;
          const sibs = parent
            ? parent.children.map((ch) => ({
                label: (ch as PIXI.Container).label ?? ch.constructor.name,
                z: (ch as PIXI.Container).zIndex,
              }))
            : [];
          return { z: this.container.zIndex, chain, siblings: sibs };
        },
      };
    }
    // The full scene rule, not just its proximity half — see computeActorZIndex.
    // Falls back to the bare proximity depth only before markers exist (spawn).
    const markers = this.getFurnitureMarkers?.();
    this.container.zIndex = markers
      ? computeActorZIndex(markers, this.tileX, this.tileY)
      : (Math.round(this.tileX) + Math.round(this.tileY)) * HALF_TILE_HEIGHT;
  }
}
